import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEntitlement } from "@/context/EntitlementContext";
import { useTheme } from "@/context/ThemeContext";
import { LayoutDashboard, History as HistoryIcon, Mail, Settings as SettingsIcon, LogOut, Crown, FileText, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";

const NavLink = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
      active ? "bg-[#E7F0EB] text-[#2D4A3E] font-medium" : "text-[#5C6661] hover:bg-[#F2F4F3]"
    }`}
  >
    <Icon className="w-4 h-4" strokeWidth={1.75} /> {label}
  </Link>
);

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { active: isPro } = useEntitlement();
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const nav = useNavigate();

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <aside className="hidden md:flex md:flex-col w-64 border-r border-[#E2E8E4] bg-white/70 backdrop-blur-xl sticky top-0 h-screen p-6">
      <Link to="/" className="flex items-center mb-10">
        <Logo size={32} />
      </Link>

      <nav className="flex flex-col gap-1">
        <NavLink to="/app" icon={LayoutDashboard} label="Discover" active={loc.pathname === "/app"} />
        <NavLink to="/app/review" icon={Mail} label="Review & Send" active={loc.pathname.startsWith("/app/review")} />
        <NavLink to="/app/history" icon={HistoryIcon} label="History" active={loc.pathname.startsWith("/app/history")} />
        <NavLink to="/app/templates" icon={FileText} label="Templates" active={loc.pathname.startsWith("/app/templates")} />
        <NavLink to="/app/smtp" icon={Mail} label="Email Setup" active={loc.pathname.startsWith("/app/smtp")} />
        <NavLink to="/app/settings" icon={SettingsIcon} label="Settings" active={loc.pathname.startsWith("/app/settings")} />
      </nav>

      {!isPro && (
        <Link to="/pricing" data-testid="sidebar-upgrade-cta" className="mt-6 block rounded-xl border border-[#2D4A3E]/20 bg-[#E7F0EB]/40 p-4 hover:bg-[#E7F0EB]/70 transition-colors">
          <div className="flex items-center gap-1.5 text-[#2D4A3E]"><Crown className="w-3.5 h-3.5" /><span className="text-xs font-semibold uppercase tracking-widest">Free plan</span></div>
          <div className="mt-2 text-sm font-medium text-[#121413]">Unlock unlimited companies</div>
          <div className="text-xs text-[#5C6661] mt-0.5">£4.99/month — cancel anytime</div>
        </Link>
      )}
      {isPro && (
        <div className="mt-6 rounded-xl border border-[#2D4A3E]/20 bg-[#E7F0EB]/70 p-4">
          <Badge className="bg-[#2D4A3E] text-white border-transparent"><Crown className="w-3 h-3 mr-1" />JobKnocker Pro</Badge>
          <div className="text-xs text-[#5C6661] mt-2">Unlimited everything</div>
        </div>
      )}

      <div className="mt-auto pt-6 border-t border-[#E2E8E4]">
        <div className="mb-3">
          <div className="overline">Signed in as</div>
          <div className="text-sm font-medium text-[#121413] truncate mt-1" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-[#5C6661] truncate">{user?.email}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleLogout} variant="outline" size="sm"
                  className="flex-1 rounded-full border-[#E2E8E4] text-[#5C6661] hover:text-[#121413]"
                  data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
          </Button>
          <button
            type="button"
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="theme-toggle"
            className="h-9 w-9 shrink-0 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C6661] hover:text-[#121413] hover:border-[#2D4A3E]/40 transition-colors"
          >
            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </aside>
  );
}
